import { Component, computed, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';

import { EmailNode } from '@modules/campaigns/Domain/Nodes/EmailNode';
import { EmailConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { SendMessageInspectorBase } from '../send-message-inspector.base';

@Component({
  selector: 'app-email-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    DropdownModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './email-inspector.component.html',
  styleUrls: ['./email-inspector.component.scss'],
})
export class EmailInspectorComponent extends SendMessageInspectorBase<EmailNode, EmailConfig> {
  protected override get channelType() {
    return 'email';
  }
  protected override get i18nPrefix() {
    return 'DATA.MODULES.INSPECTORS.EMAIL';
  }

  readonly selectedTemplateId = signal<string | null>(null);

  readonly previewSubject = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';
    const block = template.blocks?.find((b) => b.type === 'subject' || b.alias === 'subject');
    return block?.translations?.[0]?.text ?? '';
  });

  override get copyIds(): string[] {
    const id = this.selectedTemplateId();
    return id ? [id] : [];
  }

  protected override setCopyIds(ids: string[]) {
    this.selectedTemplateId.set(ids[0] ?? null);
  }

  protected override refreshCopyFromConfig(config: EmailConfig) {
    this.selectedTemplateId.set(config.copyIds?.[0] ?? null);
  }

  override get nodeValue(): EmailNode | null {
    const n = this.node();
    return n?.type === NodeType.SendEmail ? (n as EmailNode) : null;
  }

  protected override buildConfig(node: EmailNode, copyIds: string[]): EmailConfig {
    return {
      ...(node.config as EmailConfig),
      copyIds,
      copyId: copyIds[0] ?? null,
    } as EmailConfig;
  }

  protected override extractPreviewMessage(template: CopyModel | null): string {
    if (!template) return '';
    const htmlBlock = template.blocks?.find(
      (b) =>
        b.type === 'emailHtml' ||
        b.type === 'html' ||
        b.alias === 'emailHtml' ||
        b.type === 'text' ||
        b.alias === 'text',
    );
    if (htmlBlock) return htmlBlock.translations?.[0]?.text ?? '';

    const textBlock = template.blocks?.find(
      (b) => b.type === 'emailText' || b.type === 'text' || b.alias === 'emailText',
    );
    return textBlock?.translations?.[0]?.text ?? '';
  }

  onTemplateChange(id: string) {
    this.selectedTemplateId.set(id);
    this.scheduleApplyChanges();
  }

  constructor(
    workflow: WorkflowStateService,
    translate: TranslateService,
    copiesService: CopiesService,
    route: ActivatedRoute,
  ) {
    super(workflow, translate, copiesService, route);
  }
}
